import React, { useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { getCurrencyLabel } from "@/lib/currency";

interface PortfolioCurrencySelectProps {
  portfolioId: string;
  value?: string;
  onChange?: (code: string) => void;
  className?: string;
  label?: string;
}

const LS_KEY_PREFIX = "portfolio:currency:";

export function PortfolioCurrencySelect({
  portfolioId,
  value,
  onChange,
  className,
  label = "Currency",
}: PortfolioCurrencySelectProps) {
  const [currencies, setCurrencies] = useState<Array<{ code: string; name?: string; symbol?: string }>>([]);
  const [internalValue, setInternalValue] = useState<string>(value || "");
  
  const symbolMap: Record<string, string> = {
    CAD: '$',
    USD: '$',
    EUR: '€',
    GBP: '£',
    AUD: '$',
    NZD: '$',
    JPY: '¥',
    CHF: 'Fr',
  };

  const currentValue = useMemo(() => value ?? internalValue, [value, internalValue]);

  // Load currencies and default currency
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data: cur } = await supabase
          .from('currencies')
          .select('code, name, symbol')
          .eq('is_active', true)
          .order('code');
        if (!cancelled && Array.isArray(cur)) {
          const list = (cur as any[]).slice().sort((a, b) => String(a.code).localeCompare(String(b.code)));
          const preferred = ["CAD", "USD"];
          const preferredItems = preferred
            .map((p) => list.find((c: any) => String(c.code).toUpperCase() === p))
            .filter(Boolean) as any[];
          const rest = list.filter((c: any) => !preferred.includes(String(c.code).toUpperCase()));
          setCurrencies([...preferredItems, ...rest] as any);
        }
      } catch {
        // Fallback if table not present or RLS blocks
        if (!cancelled) setCurrencies([
          { code: 'CAD', name: 'Canadian Dollar', symbol: symbolMap.CAD },
          { code: 'USD', name: 'US Dollar', symbol: symbolMap.USD },
          { code: 'EUR', name: 'Euro', symbol: symbolMap.EUR },
          { code: 'GBP', name: 'British Pound', symbol: symbolMap.GBP },
          { code: 'AUD', name: 'Australian Dollar', symbol: symbolMap.AUD },
        ]);
      }

      // Resolve initial value from localStorage or user profile default
      try {
        const fromLs = localStorage.getItem(LS_KEY_PREFIX + portfolioId);
        if (fromLs) {
          if (!cancelled) {
            setInternalValue(fromLs);
            onChange?.(fromLs);
          }
          return;
        }
      } catch {}

      try {
        const { data: sess } = await supabase.auth.getSession();
        const uid = sess?.session?.user?.id;
        if (uid) {
          const { data: prof } = await supabase
            .from('user_profiles')
            .select('default_currency')
            .eq('id', uid)
            .single();
          const dc = (prof as any)?.default_currency || 'CAD';
          if (!cancelled) {
            setInternalValue(dc);
            onChange?.(dc);
          }
        } else if (!cancelled) {
          setInternalValue('CAD');
          onChange?.('CAD');
        }
      } catch {
        if (!cancelled) {
          setInternalValue('CAD');
          onChange?.('CAD');
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [portfolioId, onChange]);

  const handleChange = (code: string) => {
    setInternalValue(code);
    try { localStorage.setItem(LS_KEY_PREFIX + portfolioId, code); } catch {}
    onChange?.(code);
  };

  return (
    <div className={"min-w-[110px] " + (className || "")}> 
      <Label className="text-xs" htmlFor="portfolioCurrencySelect">{label}</Label>
      <Select value={currentValue} onValueChange={handleChange}>
        <SelectTrigger id="portfolioCurrencySelect" className="h-9">
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          {(currencies?.length ? currencies : [
            { code: 'CAD', symbol: symbolMap.CAD },
            { code: 'USD', symbol: symbolMap.USD },
            { code: 'EUR', symbol: symbolMap.EUR },
            { code: 'GBP', symbol: symbolMap.GBP },
            { code: 'AUD', symbol: symbolMap.AUD },
          ]).map((c) => (
            <SelectItem key={c.code} value={c.code}>
              {getCurrencyLabel(c.code)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default PortfolioCurrencySelect;
